import { useMemo, useState } from 'react'
import { Building2, Loader2, Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  useListAssociatedPartiesForProduct,
  type AssociatedParty,
} from '@/shared/hooks/api/associatedParties'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from '@/components/ui/empty'
import { ProductManufacturerSelectDialog } from './ProductManufacturerSelectDialog'
import { formatPartyCityCountry } from '@/features/associated-parties/formatAddress'
import { useCountryDisplayName } from '@/features/associated-parties/useCountryDisplayName'

const MANUFACTURER_ROLE = 'MANUFACTURER' as const

type ProductManufacturerCardProps = {
  productId: string
}

function formatPartyContact(party: AssociatedParty): string | null {
  const parts = [
    party.contactName,
    party.contactEmail,
    party.contactPhoneNumber,
  ]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
  return parts.length > 0 ? parts.join(' | ') : null
}

export function ProductManufacturerCard({
  productId,
}: ProductManufacturerCardProps) {
  const { t } = useTranslation('productCatalog')
  const getCountryName = useCountryDisplayName()
  const [dialogOpen, setDialogOpen] = useState(false)
  const {
    data: assignments = [],
    isLoading,
    isError,
  } = useListAssociatedPartiesForProduct(productId)

  const manufacturer = useMemo(
    () =>
      assignments.find((assignment) => assignment.role === MANUFACTURER_ROLE)
        ?.associatedParty ?? null,
    [assignments]
  )

  const location = manufacturer
    ? formatPartyCityCountry(manufacturer, getCountryName)
    : null
  const contact = manufacturer ? formatPartyContact(manufacturer) : null

  return (
    <>
      <Card className='h-fit'>
        <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-2 space-y-0'>
          <CardTitle className='text-base leading-tight'>
            {t('detail.manufacturer.title')}
          </CardTitle>
          {manufacturer ? (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8 shrink-0 text-muted-foreground hover:text-foreground'
              aria-label={t('detail.manufacturer.editAriaLabel')}
              onClick={() => setDialogOpen(true)}
            >
              <Pencil className='size-4' aria-hidden />
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-6'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          ) : isError ? (
            <p className='text-sm text-muted-foreground'>
              {t('detail.manufacturer.loadError')}
            </p>
          ) : manufacturer ? (
            <Link
              to={`/associated-parties/${manufacturer.id}`}
              className='flex items-start gap-3 rounded-lg border bg-background p-3 transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none'
            >
              <Building2 className='mt-0.5 size-5 shrink-0 text-primary' />
              <div className='min-w-0 space-y-1'>
                <p className='truncate text-sm font-medium'>
                  {manufacturer.name}
                </p>
                {location ? (
                  <CardDescription className='text-xs'>
                    {location}
                  </CardDescription>
                ) : null}
                {contact ? (
                  <CardDescription className='text-xs'>
                    {contact}
                  </CardDescription>
                ) : null}
              </div>
            </Link>
          ) : (
            <Empty className='border-0 px-0 py-2'>
              <EmptyHeader>
                <EmptyDescription>
                  {t('detail.manufacturer.empty')}
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button size='sm' onClick={() => setDialogOpen(true)}>
                  <Plus className='size-4' aria-hidden />
                  {t('detail.manufacturer.select')}
                </Button>
              </EmptyContent>
            </Empty>
          )}
        </CardContent>
      </Card>
      <ProductManufacturerSelectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        productId={productId}
        currentManufacturerId={manufacturer?.id ?? null}
      />
    </>
  )
}
